import { Backdrop, Box, Button, Grid, Typography } from "@mui/material";
import AppLoader from "../../../components/AppLoader/AppLoader";
import ReuploadApiResponseDialog from "../../../components/ReuploadDocuments/ReuploadApiResponseDialog";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import "./MeterReadingCorrectionForm.scss";
import "../DueDateExtentsion/DueDateExtensionWorkflowDialog.scss";
import { cfpBaseUrl } from "../../../network/urls";
import { documentValidation, isImage, isPdf } from "../../../services/Functions";
import { documentExtensions, portalName } from "../../../utils/PortalName";
import OpenBase64ImageInPopup from "./MrcOpenBase64ImageInPopup";
import tickIcon from "../../../images/Accept.png";
import crossIcon from "../../../images/remove.png";
import pdfIcon from "../../../images/pdfIcon.png";
import {
  formatDisplayValue,
  formatUnitsAdjDisplay,
  formatUnitsRevisedDisplay,
  getMrcEvidenceRemarksLabel,
  getMrcEvidenceRemarksVariant,
  isMdiMeterRow,
  mapDocumentsToReadingRows,
  calcUnitsAdjusted,
  calcUnitsChargedRevised,
} from "./mrcReadingUtils";

const sortReadingRows = (rows) =>
  [...rows].sort((a, b) => {
    const setDiff = Number(a.setNo) - Number(b.setNo);
    if (setDiff !== 0) return setDiff;
    return Number(a.meterId) - Number(b.meterId);
  });

const statusLabel = (flag, hasImage) => {
  if (!hasImage) return "N/A";
  if (flag === 1) return "Accepted";
  if (flag === -1) return "Rejected";
  return "Pending";
};

const MeterReadingCorrectionUpdateImages = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { token } = useSelector((state) => state.auth);
  const dialogRowData = state?.dialogRowData || {};
  const applicationId =
    dialogRowData.applicationId || dialogRowData.complaintId || "";

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [readingRows, setReadingRows] = useState([]);
  const [apiResponseDialog, setApiResponseDialog] = useState({
    open: false,
    type: "success",
    message: "",
  });
  const [activeRow, setActiveRow] = useState(null);
  const [uploadedImages, setUploadedImages] = useState({});

  // Fetch documents on mount
  useEffect(() => {
    if (!applicationId) return;
    fetchMRCDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const fetchMRCDocuments = async () => {
    setIsLoading(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      myHeaders.append("Content-Type", "application/json");

      const response = await fetch(cfpBaseUrl + "mrc/getMRCDocuments", {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify({ applicationId }),
        redirect: "follow",
      });

      const result = await response.json();

      if (String(result?.status) === "200" && Array.isArray(result?.data)) {
        setDocuments(result.data);
        const rows = sortReadingRows(mapDocumentsToReadingRows(result.data));
        setReadingRows(rows);
      } else {
        toast.error(result?.message || "Failed to fetch documents");
      }
    } catch (error) {
      toast.error("Error fetching documents");
    } finally {
      setIsLoading(false);
    }
  };

  const hideImagePopup = () => setActiveRow(null);

  const isLocallyReuploaded = (row) =>
    Boolean(row && (uploadedImages[row.id] || row.hasNewImage || row.newImageSrc));

  const getEvidencePreviewRow = (row) => {
    if (!row || !isLocallyReuploaded(row)) return row;
    return { ...row, isDocumentReuploaded: true, imageFlag: null };
  };

  // Handle image upload for rejected documents
  const handleImageUpload = (rowId, event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    const maxSize = 2 * 1024 * 1024; // 2MB
    const documentValidate = documentValidation(file, maxSize);

    if (documentValidate === 1) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImages((prev) => ({
          ...prev,
          [rowId]: e.target.result,
        }));
        // Update the row with new image
        setReadingRows((prev) =>
          prev.map((row) =>
            row.id === rowId
              ? { ...row, newImageSrc: e.target.result, hasNewImage: true }
              : row
          )
        );
      };
      reader.readAsDataURL(file);
    } else if (documentValidate === 2) {
      toast.error("File size exceeds 2MB limit");
    } else {
      toast.error("Invalid file format");
    }
  };

  // Check if any rejected image has been reuploaded
  const hasRejectedImagesReuploaded = () => {
    const rejectedRows = readingRows.filter(
      (row) => row.imageFlag === -1 && !isMdiMeterRow(row)
    );
    if (rejectedRows.length === 0) return false;
    return rejectedRows.every((row) => row.hasNewImage || uploadedImages[row.id]);
  };

  // Submit reuploaded documents
  const handleSubmit = async () => {
    const rejectedRows = readingRows.filter(
      (row) => row.imageFlag === -1 && !isMdiMeterRow(row)
    );

    if (rejectedRows.length === 0) {
      toast.info("No rejected documents to reupload");
      return;
    }

    const allReuploaded = rejectedRows.every(
      (row) => row.hasNewImage || uploadedImages[row.id]
    );

    if (!allReuploaded) {
      toast.error("Please upload images for all rejected documents");
      return;
    }

    setIsSubmitting(true);

    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      myHeaders.append("Content-Type", "application/json");

      // Only send meters that were actually reuploaded (rejected + new image)
      const meterData = readingRows
        .filter((row) => !isMdiMeterRow(row))
        .filter(
          (row) =>
            row.imageFlag === -1 &&
            Boolean(uploadedImages[row.id] || row.hasNewImage)
        )
        .map((row) => ({
          meterId: Number(row.meterId) || 0,
          setNo: Number(row.setNo) || 0,
          tsNo: Number(row.tsNo) || 0,
          consumerClaimedImage:
            uploadedImages[row.id] || row.newImageSrc || "",
          isDocumentReuploaded: true,
        }));

      if (!meterData.length) {
        toast.error("Please upload images for rejected documents");
        setIsSubmitting(false);
        return;
      }

      const payload = {
        applicationId,
        applicationProgressId: dialogRowData.applicationProgressId || 1,
        objectionStatus: 2,
        submittedThrough: portalName,
        remarks: "Documents Reuploaded",
        meterData,
      };

      const response = await fetch(cfpBaseUrl + "mrc/reUploadMRCDocuments", {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify(payload),
        redirect: "follow",
      });

      const result = await response.json();

      if (String(result?.status) === "200" || result?.status === 200) {
        // Update local storage if needed
        try {
          const lclStrdMrcConApp = JSON.parse(
            window.localStorage.getItem("meter-reading-complaints")
          );
          if (Array.isArray(lclStrdMrcConApp)) {
            const index = lclStrdMrcConApp.findIndex(
              (item) => item.applicationId === applicationId
            );
            if (index !== -1) {
              lclStrdMrcConApp[index].objectionStatus = 2;
              window.localStorage.setItem(
                "meter-reading-complaints",
                JSON.stringify(lclStrdMrcConApp)
              );
            }
          }
        } catch (e) {
          // Ignore localStorage errors
        }
        setApiResponseDialog({
          open: true,
          type: "success",
          message:
            result?.message ||
            "Documents re-uploaded successfully.",
        });
      } else {
        setApiResponseDialog({
          open: true,
          type: "error",
          message: result?.message || "Failed to reupload documents",
        });
      }
    } catch (error) {
      setApiResponseDialog({
        open: true,
        type: "error",
        message: "Error submitting documents",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApiResponseDialogClose = () => {
    const wasSuccess = apiResponseDialog.type === "success";
    setApiResponseDialog((prev) => ({ ...prev, open: false }));
    if (wasSuccess) {
      navigate("/meter-reading-correction-table");
    }
  };

  const rejectedCount = readingRows.filter(
    (row) => row.imageFlag === -1 && !isMdiMeterRow(row)
  ).length;

  const acceptedCount = readingRows.filter(
    (row) => row.imageFlag === 1 && !isMdiMeterRow(row)
  ).length;

  const pendingCount = readingRows.filter(
    (row) => row.imageFlag !== 1 && row.imageFlag !== -1 && !isMdiMeterRow(row) && row.imageSrc
  ).length;

  const uploadedCount = readingRows.filter(
    (row) => row.imageFlag === -1 && !isMdiMeterRow(row) && (row.hasNewImage || uploadedImages[row.id])
  ).length;

  return (
    <Grid container className="mrc-page">
      <Box className="mrc-container">
        <h3 className="heading mrc-page-title">
          Meter Reading Correction - Document Reupload
        </h3>

        <div className="mrc-form">
          {/* Application Details */}
          <div className="section-card section-card--accent">
            <div className="section-title">Application Details</div>
            <Grid container spacing={2} className="container">
              <Grid item xs={12} sm={6} md={4}>
                <div className="field-wrap">
                  <Typography className="fieldLabel">Complaint Id</Typography>
                  <div
                    style={{
                      padding: "10px 14px",
                      background: "#f3f6fb",
                      borderRadius: "8px",
                      border: "1px solid #dde5ef",
                      fontWeight: 600,
                      color: "#1565c0",
                    }}
                  >
                    {applicationId || "—"}
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} sm={8} md={8}>
                <div className="field-wrap">
                  <Typography className="fieldLabel">Document Status</Typography>
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        padding: "8px 16px",
                        background: "linear-gradient(135deg, #dcfce7, #bbf7d0)",
                        color: "#15803d",
                        borderRadius: "8px",
                        fontSize: "13px",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        boxShadow: "0 2px 4px rgba(34,197,94,0.15)",
                      }}
                    >
                      <span style={{ fontSize: "16px" }}>✓</span>
                      Accepted: {acceptedCount}
                    </span>
                    {rejectedCount > 0 && (
                      <span
                        style={{
                          padding: "8px 16px",
                          background: uploadedCount === rejectedCount
                            ? "linear-gradient(135deg, #dbeafe, #bfdbfe)"
                            : "linear-gradient(135deg, #fee2e2, #fecaca)",
                          color: uploadedCount === rejectedCount ? "#1d4ed8" : "#b91c1c",
                          borderRadius: "8px",
                          fontSize: "13px",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          boxShadow: uploadedCount === rejectedCount
                            ? "0 2px 4px rgba(59,130,246,0.15)"
                            : "0 2px 4px rgba(239,68,68,0.15)",
                        }}
                      >
                        <span style={{ fontSize: "16px" }}>{uploadedCount === rejectedCount ? "↑" : "✕"}</span>
                        Rejected: {uploadedCount}/{rejectedCount} Reuploaded
                      </span>
                    )}
                    {pendingCount > 0 && (
                      <span
                        style={{
                          padding: "8px 16px",
                          background: "#f1f5f9",
                          color: "#64748b",
                          borderRadius: "8px",
                          fontSize: "13px",
                          fontWeight: 700,
                        }}
                      >
                        Pending: {pendingCount}
                      </span>
                    )}
                  </div>
                </div>
              </Grid>
            </Grid>
          </div>

          {/* Meter Reading Documents Table */}
          <div className="section-card section-card--meter">
            <div className="section-title">Meter Reading Documents</div>
            {rejectedCount > 0 && (
              <p className="mrc-meter-hint">
                <strong>Note:</strong> Only <strong>Rejected</strong> documents
                need to be reuploaded. Accepted documents are shown for
                reference only.
              </p>
            )}

            {isLoading ? null : readingRows.length === 0 ? (
              <div
                style={{
                  padding: "40px",
                  textAlign: "center",
                  color: "#64748b",
                }}
              >
                No meter reading documents found.
              </div>
            ) : (
              <div className="mrc-documents-reading-table mrc-documents-reading-table--reupload">
                <div className="mrc-meter-table">
                  <div className="mrc-meter-table__head">
                    <table>
                      <colgroup>
                        <col className="col-set" />
                        <col className="col-meter" />
                        <col className="col-claimed" />
                        <col className="col-num" />
                        <col className="col-revised" />
                        <col className="col-image" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Set</th>
                          <th>Meter</th>
                          <th>User Claimed</th>
                          <th>Units Adj.</th>
                          <th>
                            Units
                            <br />
                            Revised
                          </th>
                          <th>Image</th>
                        </tr>
                      </thead>
                    </table>
                  </div>
                  <div className="mrc-meter-table__body">
                    <table>
                      <colgroup>
                        <col className="col-set" />
                        <col className="col-meter" />
                        <col className="col-claimed" />
                        <col className="col-num" />
                        <col className="col-revised" />
                        <col className="col-image" />
                      </colgroup>
                      <tbody>
                      {readingRows.map((row, index) => {
                        const isMdi = isMdiMeterRow(row);
                        const flag = row.imageFlag;
                        const currentImageSrc =
                          uploadedImages[row.id] ||
                          row.newImageSrc ||
                          row.imageSrc;
                        const hasImage = Boolean(currentImageSrc) && !isMdi;
                        const isRejected = flag === -1;
                        const isAccepted = flag === 1;
                        const statusClass = isAccepted
                          ? "is-accepted"
                          : isRejected
                            ? "is-rejected"
                            : hasImage
                              ? "is-pending"
                              : "is-na";
                        const isNewSet =
                          index === 0 ||
                          readingRows[index - 1].setNo !== row.setNo;

                        const needsAction = isRejected && !uploadedImages[row.id] && !row.hasNewImage;

                        return (
                          <tr
                            key={row.id}
                            className={[
                              `row-${statusClass}`,
                              isNewSet ? "row-set-start" : "",
                              isMdi ? "row-mdi" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            style={{
                              background: needsAction
                                ? "linear-gradient(90deg, rgba(239,68,68,0.08) 0%, rgba(239,68,68,0.03) 100%)"
                                : uploadedImages[row.id] || row.hasNewImage
                                  ? "linear-gradient(90deg, rgba(34,197,94,0.08) 0%, rgba(34,197,94,0.03) 100%)"
                                  : undefined,
                              borderLeft: needsAction
                                ? "4px solid #ef4444"
                                : uploadedImages[row.id] || row.hasNewImage
                                  ? "4px solid #22c55e"
                                  : undefined,
                            }}
                          >
                            <td className="cell-text cell-set">
                              <span
                                className={`set-badge${isNewSet ? "" : " set-badge--muted"}`}
                              >
                                {formatDisplayValue(row.setNo)}
                              </span>
                            </td>
                            <td className="cell-text cell-meter">
                              <span
                                className="meter-type-pill"
                                title={String(row.meterLabel || "").replace(/\n/g, " ")}
                              >
                                {row.meterLabel}
                              </span>
                            </td>
                            <td className="cell-text cell-num cell-claimed">
                              {isMdi ? "—" : formatDisplayValue(row.claimed)}
                            </td>
                            <td className="cell-text cell-num cell-calc">
                              {isMdi ? "—" : formatUnitsAdjDisplay(calcUnitsAdjusted(row.claimed, row.asCharged, row.mf))}
                            </td>
                            <td className="cell-text cell-num cell-calc">
                              {isMdi
                                ? "—"
                                : formatUnitsRevisedDisplay(
                                    calcUnitsChargedRevised(
                                      row.claimed,
                                      row.previous,
                                      row.mf
                                    )
                                  )}
                            </td>
                            <td className="cell-image">
                              {isMdi ? (
                                <span className="mrc-image-na">—</span>
                              ) : (
                                <div className="mrc-meter-image-wrap">
                                  {/* Image Preview with Status */}
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: "8px",
                                    }}
                                  >
                                    {/* Image Thumbnail */}
                                    {currentImageSrc ? (
                                      <button
                                        type="button"
                                        onClick={() => setActiveRow(row)}
                                        style={{
                                          position: "relative",
                                          width: "70px",
                                          height: "70px",
                                          padding: "0",
                                          border: "2px solid #e2e8f0",
                                          borderRadius: "10px",
                                          cursor: "pointer",
                                          background: "#fff",
                                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                        }}
                                        title="Click to view full image"
                                      >
                                        {isPdf(currentImageSrc) ? (
                                          <img
                                            src={pdfIcon}
                                            alt="PDF"
                                            style={{
                                              width: "42px",
                                              height: "42px",
                                              objectFit: "contain",
                                              display: "block",
                                              margin: "0 auto",
                                            }}
                                          />
                                        ) : isImage(currentImageSrc) ? (
                                          <img
                                            src={currentImageSrc}
                                            alt={`Set ${row.setNo} - ${row.meterLabel}`}
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                              borderRadius: "7px",
                                              display: "block",
                                            }}
                                            onError={(e) => {
                                              e.currentTarget.onerror = null;
                                              e.currentTarget.src = pdfIcon;
                                              e.currentTarget.style.width = "42px";
                                              e.currentTarget.style.height = "42px";
                                              e.currentTarget.style.objectFit =
                                                "contain";
                                              e.currentTarget.style.margin = "0 auto";
                                            }}
                                          />
                                        ) : (
                                          <img
                                            src={pdfIcon}
                                            alt="PDF"
                                            style={{
                                              width: "42px",
                                              height: "42px",
                                              objectFit: "contain",
                                              display: "block",
                                              margin: "0 auto",
                                            }}
                                          />
                                        )}
                                        {/* Status Icon on Image */}
                                        {isAccepted && (
                                          <img
                                            src={tickIcon}
                                            alt="Accepted"
                                            style={{
                                              position: "absolute",
                                              top: "-8px",
                                              right: "-8px",
                                              width: "24px",
                                              height: "24px",
                                              filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.2))",
                                            }}
                                          />
                                        )}
                                        {isRejected && !uploadedImages[row.id] && !row.hasNewImage && (
                                          <img
                                            src={crossIcon}
                                            alt="Rejected"
                                            style={{
                                              position: "absolute",
                                              top: "-8px",
                                              right: "-8px",
                                              width: "24px",
                                              height: "24px",
                                              filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.2))",
                                            }}
                                          />
                                        )}
                                        {(uploadedImages[row.id] || row.hasNewImage) && (
                                          <img
                                            src={tickIcon}
                                            alt="Uploaded"
                                            style={{
                                              position: "absolute",
                                              top: "-8px",
                                              right: "-8px",
                                              width: "24px",
                                              height: "24px",
                                              filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.2))",
                                            }}
                                          />
                                        )}
                                      </button>
                                    ) : (
                                      <div
                                        style={{
                                          width: "70px",
                                          height: "70px",
                                          borderRadius: "10px",
                                          background: "#f1f5f9",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          color: "#94a3b8",
                                          fontSize: "12px",
                                          fontWeight: 600,
                                        }}
                                      >
                                        No Image
                                      </div>
                                    )}

                                    {/* Action Button / Status */}
                                    {isRejected ? (
                                      <Button
                                        variant="contained"
                                        component="label"
                                        size="small"
                                        startIcon={<FileUploadOutlinedIcon fontSize="small" />}
                                        sx={{
                                          textTransform: "none",
                                          borderRadius: "8px",
                                          padding: "4px 12px",
                                          fontSize: "11px",
                                          fontWeight: 700,
                                          minWidth: "auto",
                                          background:
                                            uploadedImages[row.id] || row.hasNewImage
                                              ? "linear-gradient(135deg, #22c55e, #16a34a)"
                                              : "linear-gradient(135deg, #ef4444, #dc2626)",
                                          boxShadow:
                                            uploadedImages[row.id] || row.hasNewImage
                                              ? "0 2px 8px rgba(34,197,94,0.4)"
                                              : "0 2px 8px rgba(239,68,68,0.4)",
                                          "&:hover": {
                                            background:
                                              uploadedImages[row.id] || row.hasNewImage
                                                ? "linear-gradient(135deg, #16a34a, #15803d)"
                                                : "linear-gradient(135deg, #dc2626, #b91c1c)",
                                          },
                                        }}
                                      >
                                        {uploadedImages[row.id] || row.hasNewImage ? "Uploaded ✓" : "Reupload"}
                                        <input
                                          hidden
                                          accept={documentExtensions}
                                          type="file"
                                          onChange={(e) => handleImageUpload(row.id, e)}
                                        />
                                      </Button>
                                    ) : (
                                      <span
                                        style={{
                                          padding: "4px 12px",
                                          borderRadius: "6px",
                                          fontSize: "11px",
                                          fontWeight: 700,
                                          textTransform: "uppercase",
                                          background: isAccepted ? "#dcfce7" : "#f1f5f9",
                                          color: isAccepted ? "#15803d" : "#64748b",
                                        }}
                                      >
                                        {isAccepted ? "Accepted" : "Pending"}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Submit Area */}
          {rejectedCount > 0 && (
            <div className="submit-area">
              <Button
                variant="outlined"
                className="back-btn"
                onClick={() => navigate("/meter-reading-correction-table")}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                variant="contained"
                className="submit-btn"
                onClick={handleSubmit}
                disabled={isSubmitting || !hasRejectedImagesReuploaded()}
              >
                Submit
              </Button>
            </div>
          )}

          {rejectedCount === 0 && !isLoading && readingRows.length > 0 && (
            <div className="submit-area">
              <Button
                variant="contained"
                className="submit-btn"
                onClick={() => navigate("/meter-reading-correction-table")}
              >
                Back to List
              </Button>
            </div>
          )}
        </div>

        {/* Image Preview Popup */}
        {activeRow?.imageSrc ||
        uploadedImages[activeRow?.id] ||
        activeRow?.newImageSrc ? (
          <OpenBase64ImageInPopup
            docVerifyPopUp={Boolean(activeRow)}
            hideDocVerifyPopup={hideImagePopup}
            documentName={`${activeRow.meterLabel} — Set ${activeRow.setNo}`}
            documentImage={
              uploadedImages[activeRow?.id] ||
              activeRow?.newImageSrc ||
              activeRow?.imageSrc
            }
            width="100%"
            document={
              uploadedImages[activeRow?.id] ||
              activeRow?.newImageSrc ||
              activeRow?.imageSrc
            }
            documentFlag={
              isLocallyReuploaded(activeRow) ? 2 : activeRow?.imageFlag
            }
            readOnly={true}
            documentRemarks={getMrcEvidenceRemarksLabel(
              getEvidencePreviewRow(activeRow)
            )}
            remarksVariant={getMrcEvidenceRemarksVariant(
              getEvidencePreviewRow(activeRow)
            )}
          />
        ) : null}

        <ToastContainer
          autoClose={5000}
          style={{ width: "50%" }}
          theme="colored"
        />
      </Box>

      <ReuploadApiResponseDialog
        open={apiResponseDialog.open}
        type={apiResponseDialog.type}
        message={apiResponseDialog.message}
        applicationId={applicationId}
        onClose={handleApiResponseDialogClose}
        successTitle="Documents Re-Uploaded"
        successEyebrow="Documents updated"
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        open={isLoading || isSubmitting}
      >
        <AppLoader label={isSubmitting ? "Submitting..." : "Loading documents..."} />
      </Backdrop>
    </Grid>
  );
};

export default MeterReadingCorrectionUpdateImages;
